/**
 * <TutorSessionThread> — le fil de la séance d'un élève, du début du live à maintenant, dans sa tuile (molecule).
 *
 * **Une piste fine, des points, pas de texte permanent** :
 *  - la piste est colorée par mode — **vert** le Dialogue, **violet** le Concentré, le contour du livre sans mode —,
 *    **hachurée** là où l'élève était sorti de l'appli (plus de 3 s), et se termine sur le repère de maintenant ;
 *  - dessus, un **point** par moment, chacun sa signature (`<ThreadDot>`, la même dans l'onglet « Fil » du centre). Deux
 *    points plus proches que la cible d'un doigt (24 px) n'en font qu'un (`threadClusters`), qui prend la couleur du
 *    plus pressant.
 *
 * **Toucher le fil ouvre l'onglet « Fil » de l'élève au centre** (`onOpenThread`) : sur un point, à sa ligne, éclairée ;
 * ailleurs, sur maintenant. Le détail se lit là-bas, pas dans la tuile. À la souris, survoler un point en montre la
 * ligne au-dessus du fil, posée sur la vidéo, sans bouton et sans place gardée (daily du 05/11) ; au clavier, ← et →
 * passent d'un point à l'autre, Entrée ouvre. Chaque point garde une cible de 24 px. Sans onglet « Fil » (le
 * téléphone), toucher un point en montre la ligne.
 *
 * **La barre part de la première présence de l'élève** (`from`) et finit à maintenant, au bout à droite : elle se
 * resserre à mesure que la séance avance, sans vide à gauche (daily du 05/11). Ses entrées dans la salle y sont des
 * points (`join`, à l'encre). Tout ce qu'il montre est un signal que le navigateur ou le serveur a vu ; l'écran le
 * construit (`tutorSessionRibbon`).
 */
import React, { useRef, useState } from "react"
import { Platform, Pressable, View, type LayoutChangeEvent, StyleSheet as RNStyleSheet } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type UITutorSessionRibbon } from "../../../ports/primary/ui/types/tutorLive"
import { Row } from "../../atoms/containers/Row"
import { Text } from "../../atoms/typography/Text"
import { MODE_COLOR, ThreadDot, ThreadHatch } from "../ThreadDot"
import { threadClusters, type ThreadCluster } from "./threadClusters"

export type TutorSessionThreadProps = UITutorSessionRibbon & {
    /**
     * Ouvrir l'onglet « Fil » de l'élève au centre : `eventId`, le point touché (le plus récent d'un point fondu), sa
     * ligne s'y éclaire ; absent, le fil s'ouvre sur maintenant. Absent (un téléphone, qui n'a pas d'onglet « Fil ») :
     * toucher un point en montre la ligne sous le fil, comme le survol.
     */
    onOpenThread?: (eventId?: string) => void
    testID?: string
}

/** La hauteur de la ligne de survol, une ligne de texte. */
const DETAIL_MIN_HEIGHT = 24
/** La cible d'un point, au doigt comme à la souris : deux points plus proches se fondent. */
const HIT = 24
const TRACK = 4
const DOT = 9

const TEXTS = {
    session: (minutes: number) => `Séance de ${minutes} min, ouvrir son fil`,
    more: (count: number) => `+${count}`,
}

type WebKeyEvent = { nativeEvent: { key: string }; preventDefault: () => void }
type Focusable = { focus?: () => void }

export function TutorSessionThread(props: TutorSessionThreadProps) {
    const { now, from = 0, modes, events, away, onOpenThread, testID } = props
    // État d'interface : la largeur mesurée (les points s'y fondent), le point dont la ligne se montre au survol.
    const [width, setWidth] = useState(0)
    const [shownId, setShownId] = useState<string | null>(null)
    const dotRefs = useRef<Record<string, Focusable | null>>({})

    const length = Math.max(now - from, Number.EPSILON)
    const pct = (minute: number) => `${Math.max(0, Math.min(100, ((minute - from) / length) * 100))}%` as const
    const span = (start: number, end: number) =>
        `${Math.max(0, ((Math.min(end, now) - Math.max(start, from)) / length) * 100)}%` as const
    const clusters = threadClusters(events, now, width, HIT, from)
    const shown = clusters.find((cluster) => cluster.id === shownId) ?? null
    const open = (cluster?: ThreadCluster) => onOpenThread?.(cluster?.events[cluster.events.length - 1].id)

    const onLayout = (event: LayoutChangeEvent) => setWidth(event.nativeEvent.layout.width)
    const move = (index: number, step: number) => {
        const next = clusters[index + step]
        if (next == null) return
        setShownId(next.id)
        dotRefs.current[next.id]?.focus?.()
    }
    const keysOf = (index: number) =>
        Platform.OS === "web"
            ? {
                  onKeyDown: (event: WebKeyEvent) => {
                      const step =
                          event.nativeEvent.key === "ArrowRight" ? 1 : event.nativeEvent.key === "ArrowLeft" ? -1 : 0
                      if (step === 0) return
                      event.preventDefault()
                      move(index, step)
                  },
              }
            : undefined

    return (
        <View testID={testID}>
            <View style={styles.root} onLayout={onLayout}>
                {/* Le fond du fil : le toucher hors d'un point ouvre le fil sur maintenant. */}
                <Pressable
                    onPress={() => open()}
                    disabled={onOpenThread == null}
                    accessibilityRole="button"
                    accessibilityLabel={TEXTS.session(Math.max(1, Math.round(now)))}
                    style={styles.background}
                />
                <View pointerEvents="none" style={styles.track}>
                    {modes.map((mode) => (
                        <View
                            key={`${mode.mode}-${mode.from}`}
                            style={styles.mode(pct(mode.from), span(mode.from, mode.to), mode.mode)}
                        />
                    ))}
                    {away != null && away.length > 0 && (
                        <ThreadHatch
                            spans={away.map((range) => ({ left: pct(range.from), width: span(range.from, range.to) }))}
                        />
                    )}
                </View>
                <View pointerEvents="none" style={styles.nowMark} />
                {clusters.map((cluster, index) => (
                    <Pressable
                        key={cluster.id}
                        ref={(node) => {
                            dotRefs.current[cluster.id] = node as Focusable | null
                        }}
                        onHoverIn={() => setShownId(cluster.id)}
                        onHoverOut={() => setShownId((current) => (current === cluster.id ? null : current))}
                        onFocus={() => setShownId(cluster.id)}
                        onBlur={() => setShownId((current) => (current === cluster.id ? null : current))}
                        onPress={() =>
                            onOpenThread != null
                                ? open(cluster)
                                : setShownId((current) => (current === cluster.id ? null : cluster.id))
                        }
                        accessibilityRole="button"
                        accessibilityLabel={cluster.events.map((event) => event.label).join(", ")}
                        style={styles.hit(`${cluster.x * 100}%`)}
                        {...keysOf(index)}
                    >
                        <ThreadDot kind={cluster.kind} size={DOT} merged={cluster.events.length > 1} />
                    </Pressable>
                ))}
            </View>
            {shown != null && <PreviewLine cluster={shown} />}
        </View>
    )
}

/** La ligne d'un point survolé, sous le fil : sa signature, ce qui s'est passé (le plus récent d'un point fondu). */
function PreviewLine(props: { cluster: ThreadCluster }) {
    const { cluster } = props
    const last = cluster.events[cluster.events.length - 1]
    const others = cluster.events.length - 1
    return (
        <View pointerEvents="none" style={styles.preview}>
            <Row gap="2" align="center" style={styles.detail}>
                <ThreadDot kind={cluster.kind} size={8} />
                <Text size="text-xs" color="text-primary" numberOfLines={1} style={styles.flex}>
                    {last.label}
                    {others > 0 ? ` · ${TEXTS.more(others)}` : ""}
                </Text>
            </Row>
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    root: { height: HIT, position: "relative" as const, justifyContent: "center" as const },
    background: { ...RNStyleSheet.absoluteFillObject },
    track: {
        height: TRACK,
        borderRadius: theme.radius.full,
        overflow: "hidden" as const,
        backgroundColor: theme.colors.border,
    },
    mode: (left: `${number}%`, width: `${number}%`, mode: "dialogue" | "focused") => ({
        position: "absolute" as const,
        left,
        width,
        top: 0,
        bottom: 0,
        backgroundColor: theme.colors[MODE_COLOR[mode]],
    }),
    nowMark: {
        position: "absolute" as const,
        right: 0,
        top: (HIT - TRACK) / 2 - 3,
        width: 2,
        height: TRACK + 6,
        borderRadius: 1,
        backgroundColor: theme.colors["text-primary"],
    },
    hit: (left: `${number}%`) => ({
        position: "absolute" as const,
        left,
        top: 0,
        width: HIT,
        height: HIT,
        marginLeft: -HIT / 2,
        alignItems: "center" as const,
        justifyContent: "center" as const,
    }),
    detail: {
        minHeight: DETAIL_MIN_HEIGHT,
        paddingHorizontal: theme.spacing["2"],
        paddingVertical: theme.spacing["1"],
        borderRadius: theme.radius.sm,
        backgroundColor: theme.colors["surface-subtle"],
    },
    /** Au-dessus du fil, sur le bas de la vidéo : la ligne ne pousse rien. */
    preview: { position: "absolute" as const, left: 0, right: 0, bottom: HIT, zIndex: 1 },
    flex: { flex: 1, minWidth: 0 },
}))
